import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, finalize, map, takeUntil } from 'rxjs/operators';
import { buildLessonUrl } from '../../../shared/helpers/lesson-url.helpers';
import { LevelProgressService } from '../../../shared/level-progress/level-progress.service';
import { LogService } from '../../../shared/log.service';
import { ProfileLiteExtService } from '../../../shared/profile-lite-ext.service';
import { ProfileServiceConfigurationService } from '../../../shared/profile-service-configuration.service';
import { PropertyLevelProgressDto } from '../../../shared/types/PropertyLevelProgressDto';
import { StudyBundleCourseDto } from '../../../shared/types/StudyBundleCourseDto';
import { GamificationActivityType } from '../../../shared/types/gamification-activity-type';
import { LessonType } from '../../../shared/types/lesson-type';
import { WindowService } from '../../../shared/window.service';
import { LessonCarouselService } from '../../lesson-carousel/lesson-carousel.service';
import MiniLessonsCalculations from '../mini-lessons-calculations';
import {
  DEFAULT_UNIT_QUIZ_COOLDOWN_HOURS,
  getUnitQuizCooldownRemaining,
  isTakeUnitQuizLocked,
  resolveUnitQuizCooldownHours,
} from '../take-unit-quiz/take-unit-quiz-lock.helpers';
import {
  TAKE_UNIT_QUIZ_CONFIRM_TITLE_ID,
  TakeUnitQuizConfirmModalComponent,
} from '../take-unit-quiz/take-unit-quiz-confirm-modal.component';
import {
  TAKE_UNIT_QUIZ_LOCKED_TITLE_ID,
  TakeUnitQuizLockedModalComponent,
} from '../take-unit-quiz/take-unit-quiz-locked-modal.component';

type TakeUnitQuizModal = typeof TakeUnitQuizConfirmModalComponent | typeof TakeUnitQuizLockedModalComponent;

@Component({
  selector: 'lp2-mini-lesson-section',
  templateUrl: './mini-lesson-section.component.html',
  styleUrls: ['./mini-lesson-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MiniLessonSectionComponent implements OnInit, OnDestroy {
  @Input()
  set activityPoints(value: Map<GamificationActivityType, number>) {
    this.lessonPoints = value ? value.get(GamificationActivityType.COMPLETE_LESSON) : 0;
  }
  @Input() unitIndexEnabled: boolean;

  lessonPoints: number;
  lessons$ = new BehaviorSubject<StudyBundleCourseDto[]>(undefined);
  allLessonsCompleted: boolean;
  unitAssessmentLesson: StudyBundleCourseDto | undefined;
  unitLevelProgress$ = new BehaviorSubject<PropertyLevelProgressDto>(undefined);

  destroy = new Subject<boolean>();

  private readonly _takeUnitQuizLoading$ = new BehaviorSubject<boolean>(false);
  readonly takeUnitQuizLoading$ = this._takeUnitQuizLoading$.asObservable();
  private readonly _takeUnitQuizEnabled$ = new BehaviorSubject<boolean>(false);
  readonly takeUnitQuizEnabled$ = this._takeUnitQuizEnabled$.asObservable();
  private readonly _takeUnitQuizError$ = new BehaviorSubject<boolean>(false);
  readonly takeUnitQuizError$ = this._takeUnitQuizError$.asObservable();
  private unitQuizCooldownHours = DEFAULT_UNIT_QUIZ_COOLDOWN_HOURS;

  get lessons() {
    return this.lessons$.value;
  }

  get unitLevelProgress() {
    return this.unitLevelProgress$.value;
  }

  get currentUnitNumber(): number {
    const progress = this.unitLevelProgress;
    return progress ? MiniLessonsCalculations.currentUnitNumber(progress.taken, progress.required) : 1;
  }

  constructor(
    private lessonService: LessonCarouselService,
    private levelProgressService: LevelProgressService,
    private readonly profileLiteExtService: ProfileLiteExtService,
    private readonly profileServiceConfigurationService: ProfileServiceConfigurationService,
    private readonly modalService: NgbModal,
    private readonly windowService: WindowService
  ) {}

  ngOnInit() {
    this.lessonService.getLessons().subscribe((data: StudyBundleCourseDto[]) => {
      this.allLessonsCompleted = MiniLessonsCalculations.areAllLessonsCompleted(data);
      this.unitAssessmentLesson = data ? data.find((lesson) => lesson.type === LessonType.UNIT_ASSESSMENT) : undefined;
      this.lessons$.next(data);

      // Uncoment the following code to complete all the lessons until Unit 7 assessment (mid-level test)
      // const unit7Id = 168;
      // const unit7AssessmentRule = (lesson) => lesson.unitId !== unit7Id || lesson.type !== LessonType.UNIT_ASSESSMENT;
      // const lessonsToBeCompleted = data.filter((lesson) => unit7AssessmentRule(lesson));
      // lessonsToBeCompleted.forEach((lesson) => {
      //   if (lesson.type === LessonType.UNIT_ASSESSMENT) {
      //     setTimeout(() =>this.lessonService.finishAssessment(lesson.id).pipe(tap(() => window.location.reload())).subscribe())
      //   }
      //   if (lesson.status !== StudyBundleCourseStatusType.COMPLETE) {
      //     console.log('test')
      //     setTimeout(() => this.lessonService.completeLesson(lesson.id).subscribe()) 
      //   }
      // })
    });

    this.levelProgressService.unitLevelProgress$
      .pipe(takeUntil(this.destroy))
      .subscribe((data: PropertyLevelProgressDto) => this.unitLevelProgress$.next(data));

    this.profileLiteExtService.state$
      .pipe(
        filter((personData) => !!personData),
        map((personData) => !!(personData.program && personData.program.takeUnitQuizEnabled)),
        takeUntil(this.destroy)
      )
      .subscribe((enabled) => this._takeUnitQuizEnabled$.next(enabled));

    this.profileServiceConfigurationService
      .unitQuizCooldownPeriodHours$()
      .pipe(takeUntil(this.destroy))
      .subscribe((hours) => (this.unitQuizCooldownHours = resolveUnitQuizCooldownHours(hours)));
  }

  ngOnDestroy() {
    this.destroy.next(true);
    this.destroy.unsubscribe();
  }

  onTakeUnitQuiz() {
    const assessment = this.unitAssessmentLesson;
    if (!assessment || this._takeUnitQuizLoading$.value) {
      return;
    }

    this._takeUnitQuizError$.next(false);
    this._takeUnitQuizLoading$.next(true);
    this.lessonService
      .getLastAssessment(assessment.id)
      .pipe(
        finalize(() => this._takeUnitQuizLoading$.next(false)),
        takeUntil(this.destroy)
      )
      .subscribe(
        (lastAssessment) => {
          if (isTakeUnitQuizLocked(lastAssessment, this.unitQuizCooldownHours)) {
            const lockedRef = this.openTakeUnitQuizModal(
              TakeUnitQuizLockedModalComponent,
              TAKE_UNIT_QUIZ_LOCKED_TITLE_ID
            );
            lockedRef.componentInstance.cooldownHours = this.unitQuizCooldownHours;
            lockedRef.componentInstance.remaining = getUnitQuizCooldownRemaining(
              lastAssessment.completedAt,
              this.unitQuizCooldownHours
            );
            lockedRef.result.catch(() => undefined);
            return;
          }

          const modalRef = this.openTakeUnitQuizModal(TakeUnitQuizConfirmModalComponent, TAKE_UNIT_QUIZ_CONFIRM_TITLE_ID);
          modalRef.componentInstance.unitNumber = this.currentUnitNumber;
          modalRef.componentInstance.nextUnitNumber = this.currentUnitNumber + 1;
          modalRef.result.then(() => this.launchTakeUnitQuiz(assessment), () => undefined);
        },
        (error: HttpErrorResponse) => {
          LogService.error('Take Unit Quiz: last assessment request failed', error && error.status);
          this._takeUnitQuizError$.next(true);
        }
      );
  }

  private openTakeUnitQuizModal(component: TakeUnitQuizModal, ariaLabelledBy: string) {
    return this.modalService.open(component, {
      ariaLabelledBy,
      backdrop: 'static',
      keyboard: false,
      centered: true,
      windowClass: 'take-unit-quiz-modal-window',
    });
  }

  private launchTakeUnitQuiz(assessment: StudyBundleCourseDto) {
    this.windowService.redirectToHref(
      buildLessonUrl(`courseId=${assessment.id}&lessonType=${assessment.lessonType}&isSkippingUnit=true`)
    );
  }
}



// WEBPACK FOOTER //
// ./ts/app/home/mini-lesson-section/mini-lesson-section/mini-lesson-section.component.ts